import { Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LoadingPlaceholderProps {
  message?: string;
  description?: string;
}

interface ErrorPlaceholderProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}

export function LoadingPlaceholder({
  message = 'Loading...',
  description = 'Please wait while we set up your workspace',
}: LoadingPlaceholderProps) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="flex flex-col items-center justify-center max-w-md text-center">
        <div className="relative mb-6">
          <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" />
          <div className="relative rounded-full bg-primary/5 p-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">{message}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export function ErrorPlaceholder({
  title = 'Unable to Load Application',
  message = 'We encountered an error while loading your data. Please try again.',
  onRetry,
  isRetrying = false,
}: ErrorPlaceholderProps) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="flex flex-col items-center justify-center max-w-md text-center">
        <div className="rounded-full bg-destructive/10 p-4 mb-6">
          <AlertCircle className="h-10 w-10 text-destructive" />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">{title}</h2>
        <p className="text-sm text-muted-foreground mb-6">{message}</p>
        {onRetry && (
          <Button
            onClick={onRetry}
            disabled={isRetrying}
            className={cn(isRetrying && 'opacity-70')}
          >
            {isRetrying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Retrying...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Try Again
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
